<template>
    <div class="page page-layout enquiry">
        <div class="flexbox-fullheight form-view page-container">
            <header class="page-header">
                <div class="page-header-background"></div>

                <template v-if="!loading">
                    <router-link class="return-to-dashboard" :to="{ name: 'dashboard', hash: '#requests' }">
                        <icon class="arrow-left-circle-svg large-svg fill-white stroke-white" name="arrow-left" />
                    </router-link>

                    <div class="grid-x">
                        <div class="cell large-8">
                            <nav class="page-breadcrumbs">
                                <ol class="page-breadcrumbs-list" role=list>
                                    <li class="page-breadcrumbs-item"><router-link class="enquiry-navigation-item" :to="{ name: 'dashboard', hash: '#requests' }">Oversigt</router-link></li>
                                    <li class="page-breadcrumbs-item" aria-current="page">
                                        <icon class="small-svg fill-white" name="chevron-right"></icon>
                                        <icon class="small-svg fill-white" name="enquiry"></icon>
                                        Henvendelse
                                    </li>
                                </ol>
                            </nav>

                            <h1 class="page-heading">
                                <template v-if="!existingEnquiry">Ny henvendelse</template>
                                <template v-else-if="addressObject && addressObject.oneLineAddress">{{ addressObject.oneLineAddress }}</template>
                                <template v-else>Rediger henvendelse</template>
                                <span class="enquiry-status" :class="statusColor(enquiry.requestStatus)" v-if="existingEnquiry && enquiry.requestStatus">{{ enquiry.requestStatus | statusName }}</span>
                                <span class="enquiry-status" v-else-if="existingEnquiry">Under vurdering</span>
                            </h1>

                            <div class="flexbox align-items flexbox-wrap" style="gap: 6px 18px;">
                                <div class="flexbox-center page-header-info">
                                    <icon class="medium-svg fill-white" name="user-reverse" />
                                    <template v-if="existingEnquiry && enquiry.requestStatus === 'Afsluttet'">
                                        <template v-if="!enquiryResponsible">
                                            <em>Inaktiv bruger</em>
                                        </template>
                                        <template v-else>
                                            {{ enquiryResponsible }}
                                        </template>
                                    </template>
                                    <n-filter :list="responsiblesFiltered" valueKey="userId" labelKey="name" :multiSelect="false" :showSearch="true" searchPlaceholder="Søg efter ansvarlig" v-model="enquiry.ownerUserId">
                                        Ansvarlig
                                        <template v-slot:buttonIcon>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="24" viewBox="0 0 32 24">
                                                <polygon points="0,0 32,0 16,24" style="fill: rgb(138, 138, 138)"/>
                                            </svg>
                                        </template>
                                    </n-filter>
                                </div>
                            </div>
                        </div>
                        <div class="cell large-2 page-header-actions">
                            <base-split-button v-if="enquiry.requestStatus !== 'Afsluttet' && enquiry.requestStatus !== 'Afvist - vedrører ikke forsyningen'" @click="save()">
                                <template v-slot:default>{{ saveStatus }}</template>
                                <template v-if="existingEnquiry" v-slot:list>
                                    <ul role="menu">
                                        <li v-if="existingEnquiry && enquiry.requestStatus !== 'Afsluttet' && enquiry.requestStatus !== 'Afvist - vedrører ikke forsyningen'" role="none">
                                            <button tabindex="0" role="menuitem" @click="openModal('enquiry-modal-close', {enquiry})">Afslut</button>
                                        </li>
                                        <li v-if="enquiry.requestId" role="none">
                                            <button tabindex="0" role="menuitem" @click="deleteEnquiry()">Slet</button>
                                        </li>
                                    </ul>
                                </template>
                            </base-split-button>
                            <base-split-button v-else-if="existingEnquiry && enquiry.requestStatus === 'Afsluttet' || existingEnquiry && enquiry.requestStatus === 'Afvist - vedrører ikke forsyningen'" @click="openEnquiry">Genåbn</base-split-button>
                        </div>
                        <div class="cell large-2 page-header-actions">
                            <div class="save-time-stamp" v-show="savedTimestamp && savedTimestamp !== null">Gemt {{ savedTimestamp | formatTime }}</div>
                        </div>
                    </div>
                </template>
            </header>

            <main class="page-content" v-show="!loading">
                <guide-nav :icons="iconObj" :initStep="step" @changeStep="changedStep" />

                <!-- ------------- step 0 ------------- -->
                <section v-if="step === 0">
                    <div class="grid-x history-header">
                        <div class="cell large-10">
                            <h2>Historik</h2>
                            <button @click="printCaseHistoryHandler()" class="button button-simplified large-width-label">
                                <icon class="bin-svg small-svg fill-dark-gray" name="printer" />
                                Udskriv sagsforløb
                            </button>
                        </div>
                    </div>

                    <history-log v-if="enquiryLog" :comments="enquiryLog" :type="'enquiry'" />
                </section>

                <!-- ------------- step 1 ------------- -->
                <section v-if="step === 1">
                    <div class="grid-x">
                        <div class="grid-x medium-6 large-6">
                            <h2>Anmelder</h2>
                        </div>
                        <div class="grid-x medium-6 large-6 flexbox-end flexbox-wrap">
                            <a @click="getUserInfo()" class="contact-button" title="Indsæt dine kontaktoplysninger">Hent mine kontaktoplysninger</a>
                        </div>
                    </div>

                    <div class="form">
                        <div class="grid-x">
                            <div class="flexbox-center">
                                <icon class="small-label-svg" name="user" />
                            </div>
                            <div class="cell small-8 medium-4 large-4 flexbox align-items">
                                <label for="clientName">Navn</label>
                            </div>
                            <div class="cell auto flexbox-start">
                                <input type="text" id="clientName" v-model="enquiry.clientName" maxlength="50">
                            </div>
                        </div>
                    </div>

                    <hr />

                    <div class="form">
                        <div class="grid-x">
                            <div class="flexbox-center">
                                <icon class="small-label-svg" name="phone" />
                            </div>
                            <div class="cell small-8 medium-4 large-4 flexbox align-items">
                                <label for="clientPhone">Telefon</label>
                            </div>
                            <div class="cell auto flexbox-start">
                                <input type="text" id="clientPhone" v-model="enquiry.clientPhone" maxlength="50">
                            </div>
                        </div>
                    </div>

                    <hr />

                    <div class="form">
                        <div class="grid-x">
                            <div class="flexbox-center">
                                <icon class="small-label-svg" name="envelope" />
                            </div>
                            <div class="cell small-8 medium-4 large-4 flexbox align-items">
                                <label for="clientEmail">E-mail</label>
                            </div>
                            <div class="cell auto flexbox-start flexbox-wrap" :class="{ 'wrong-email': !validEmailSaved }">
                                <input type="email" id="clientEmail" maxlength="255" v-model="enquiry.clientEmail" @blur="validEmailSaved = isValidEmail()" />
                                <small v-if="!validEmailSaved" class="emailErrorText">Den indtastede e-mail er ikke gyldig.</small>
                            </div>
                        </div>
                    </div>

                    <template v-if="showFields.clientType">
                        <hr />

                        <div class="form" :class="{'disable': !contactTypesFiltered || contactTypesFiltered.length === 0 || !contactTypesFiltered.length}">
                            <div class="grid-x">
                                <div class="flexbox-center">
                                    <icon class="small-label-svg" name="file-text" />
                                </div>
                                <div class="cell small-8 medium-4 large-4 flexbox align-items">
                                    <label for="contactTypesFiltered">Kontakttype</label>
                                </div>

                                <div class="cell auto flexbox-start">
                                    <select v-model="enquiry.clientTypeId" id="contactTypesFiltered" class="select-options" :disabled="!contactTypesFiltered || contactTypesFiltered.length === 0">
                                        <option value="" disabled>Vælg kontakttype</option>
                                        <optgroup v-if="recentContactTypes.length > 0" label="Senest anvendte">
                                            <option v-for="item in recentContactTypes" :value="item.typeId" :key="'recent-select-' + item.typeId">{{ item.description }}</option>
                                        </optgroup>
                                        <optgroup v-if="recentContactTypes.length > 0 && otherContactTypes.length > 0" label="Øvrige">
                                            <option v-for="item in otherContactTypes" :value="item.typeId" :key="'other-select-' + item.typeId">{{ item.description }}</option>
                                        </optgroup>
                                        <option v-else v-for="item in otherContactTypes" :value="item.typeId" :key="item.typeId">{{ item.description }}</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </template>

                    <hr />

                    <div class="form">
                        <div class="grid-x">
                            <div class="flexbox-center">
                                <icon class="small-label-svg" name="feedback" />
                            </div>
                            <div class="cell small-8 medium-4 large-4 flexbox align-items">
                                <label for="enquiryFeedback">Anmelder ønsker tilbagemelding</label>
                            </div>
                            <div class="cell auto flexbox-start align-items">
                                <input v-model="enquiry.clientResponse" id="enquiryFeedback" type="checkbox">
                            </div>
                        </div>
                    </div>

                    <hr />

                    <div class="form">
                        <div class="grid-x">
                            <div class="flexbox-center">
                                <icon class="small-label-svg" name="user-anon" />
                            </div>
                            <div class="cell small-8 medium-4 large-4 flexbox align-items">
                                <label for="enquiryAnon">Anmelder er anonym</label>
                            </div>
                            <div class="cell auto flexbox-start align-items">
                                <input v-model="enquiry.clientAnonymous" id="enquiryAnon" type="checkbox">
                            </div>
                        </div>
                    </div>
                </section>

                <!-- ------------- step 2 ------------- -->
                <section v-show="step === 2">
                    <div class="grid-x">
                        <h2>Problem</h2>
                    </div>

                    <supply-type :class="{'disable': enquiry.taskId}" />

                    <hr />

                    <div class="form">
                        <div class="grid-x">
                            <div class="flexbox-center">
                                <icon class="small-label-svg" name="calendar" />
                            </div>
                            <div class="cell small-8 medium-4 large-4 flexbox align-items">
                                <label for="datepicker">Hændelsesdato</label>
                            </div>
                            <div class="cell auto flexbox-start">
                                <base-date-picker id="datepicker" v-model="enquiry.occurrenceDateTime">Hændelsesdato</base-date-picker>
                            </div>
                        </div>
                    </div>

                    <hr />

                    <div class="form">
                        <div class="grid-x">
                            <div class="flexbox-center">
                                <icon class="small-label-svg" name="location" />
                            </div>
                            <div class="cell small-8 medium-4 large-4 flexbox align-items">
                                <label>Adresse for problem</label>
                            </div>
                            <div class="cell auto flexbox-start">
                                <n-input-address
                                    ref="addressInput"
                                    style="width: 100%"
                                    :municipality-code="userInfo.municipalityCodeList ? userInfo.municipalityCodeList.includes(',') ? userInfo.municipalityCodeList.split(',').map(Number) : Number(userInfo.municipalityCodeList) : null"
                                    :is-access-address="true"
                                    @input="addressesSet($event)"
                                    v-model="address"
                                />
                            </div>
                        </div>
                    </div>

                    <template v-if="showFields.estateType">
                        <hr />

                        <div class="form" :class="{'disable': !estateTypesFiltered || estateTypesFiltered.length === 0 || !estateTypesFiltered.length}">
                            <div class="grid-x">
                                <div class="flexbox-center">
                                    <icon class="small-label-svg" name="property" />
                                </div>
                                <div class="cell small-8 medium-4 large-4 flexbox align-items">
                                    <label for="estateTypesFiltered">Ejendomstype</label>
                                </div>
                                <div class="cell auto flexbox-start">
                                    <select v-model="enquiry.estateTypeId" id="estateTypesFiltered" class="select-options" :disabled="!estateTypesFiltered || estateTypesFiltered.length === 0">
                                        <option value="" disabled>Vælg ejendomstype</option>
                                        <optgroup v-if="recentEstateTypes.length > 0" label="Senest anvendte">
                                            <option v-for="item in recentEstateTypes" :value="item.typeId" :key="'recent-select-' + item.typeId">{{ item.description }}</option>
                                        </optgroup>
                                        <optgroup v-if="recentEstateTypes.length > 0 && otherEstateTypes.length > 0" label="Øvrige">
                                            <option v-for="item in otherEstateTypes" :value="item.typeId" :key="'other-select-' + item.typeId">{{ item.description }}</option>
                                        </optgroup>
                                        <option v-else v-for="item in otherEstateTypes" :value="item.typeId" :key="item.typeId">{{ item.description }}</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </template>

                    <hr />

                    <div class="form" :class="{'disable': !problemTypesFiltered || problemTypesFiltered.length === 0 || !problemTypesFiltered.length}">
                        <div class="grid-x">
                            <div class="flexbox-center">
                                <icon class="small-label-svg" name="file-text" />
                            </div>
                            <div class="cell small-8 medium-4 large-4 flexbox align-items">
                                <label for="problemTypesFiltered">Problemtype</label>
                            </div>
                            <div class="cell auto flexbox-start">
                                <n-filter :list="problemTypesFiltered" valueKey="cProblemTypeId" labelKey="description" :multiSelect="false" :showSearch="true" searchPlaceholder="Søg efter problemtype" v-model="enquiry.problemTypeId" @input="problemTypeSelected">
                                    Problemtype
                                </n-filter>
                            </div>
                        </div>
                        <div class="grid-x">
                            <div class="flexbox-center" style="width: 40px;">
                                &nbsp;
                            </div>
                            <div class="cell small-8 medium-4 large-4 flexbox align-items">
                                &nbsp;
                            </div>
                            <div class="cell auto flexbox-start">
                                <p>Rekvireres: <strong>{{ createRequisition(enquiry.problemTypeId) }}</strong></p>
                            </div>
                        </div>
                    </div>

                    <template v-if="showFields.estateType">
                        <hr />

                        <div class="form" :class="{'disable': !weatherTypesFiltered || weatherTypesFiltered.length === 0 || !weatherTypesFiltered.length}">
                            <div class="grid-x">
                                <div class="flexbox-center">
                                    <icon class="small-label-svg" name="weather" />
                                </div>
                                <div class="cell small-8 medium-4 large-4 flexbox align-items">
                                    <label for="weatherTypesFiltered">Vejrtype</label>
                                </div>
                                <div class="cell auto flexbox-start">
                                    <select v-model="enquiry.weatherTypeId" id="weatherTypesFiltered" class="select-options">
                                        <option v-if="!weatherTypesFiltered &&  !selectedSupplyType.id || weatherTypesFiltered && !selectedSupplyType.id && weatherTypesFiltered.length === 0" selected="selected" value="" disabled>Vælg forretningsområde først</option>
                                        <option v-if="!weatherTypesFiltered && selectedSupplyType.id && weatherTypesFiltered.length === 0 || weatherTypesFiltered && selectedSupplyType.id && weatherTypesFiltered.length === 0 || weatherTypesFiltered && selectedSupplyType.id && !weatherTypesFiltered.length" value="" disabled>Ingen vejrtyper for {{selectedSupplyTypeDescription | toLowerCase}}</option>
                                        <option value="" disabled="disabled">Vælg vejrtype</option>
                                        <option v-for="weatherType in weatherTypesFiltered" :key="weatherType.typeId" :value="weatherType.typeId">{{weatherType.description}}</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </template>

                    <template v-if="showFields.complaint">
                        <hr />

                        <div class="form">
                            <div class="grid-x">
                                <div class="flexbox-center">
                                    <icon class="small-label-svg" name="user-anon" />
                                </div>
                                <div class="cell small-8 medium-4 large-4 flexbox align-items">
                                    <label for="enquiryAnon">Klage</label>
                                </div>
                                <div class="cell auto flexbox align-items">
                                    <input v-model="enquiry.complaint" id="enquiryAnon" type="checkbox">
                                </div>
                            </div>
                        </div>
                    </template>

                    <hr />

                    <div class="form">
                        <div class="grid-x">
                            <div class="flexbox-center">
                                <icon name="bubble" class="small-label-svg" />
                            </div>
                            <div class="cell small-8 medium-4 large-4 flexbox align-items">
                                <label for="description">Beskrivelse</label>
                            </div>
                            <div class="cell auto flexbox-start">
                                <textarea v-model="enquiry.description" id="description" rows="4" cols="30" class="text-area-form"></textarea>
                            </div>
                        </div>
                    </div>

                    <hr />

                    <div class="form">
                        <div class="grid-x">
                            <div class="flexbox-center">
                                <icon name="file-text" class="small-label-svg" />
                            </div>
                            <div class="cell small-8 medium-4 large-4 flexbox align-items">
                                <label for="enquiry-description-show">Vis beskrivelse p&aring; entrepren&oslash;ropgave og i bestillingsmail</label>
                            </div>
                            <div class="auto cell flexbox-start">
                                <input type="checkbox" id="enquiry-description-show" v-model="enquiry.showDescriptionOnRequisition" />
                            </div>
                        </div>
                    </div>
                </section>

                <!-- ------------- step 3 ------------- -->
                <section v-if="step === 3">
                    <n-files :allowedFileTypes="applicationSettings.allowedFileTypes.join(',')" :showList="false" :uploadFunction="uploadFiles" :uploadStatus="uploadStatus" :uploadErrorResponse="uploadErrorResponse">
                        <template v-slot:heading>
                            Dokumenter
                        </template>
                        <template v-slot:description>
                            Upload dine billeder og dokumenter.<br />
                            <em>F.eks. *.pdf, *.jpg, *.doc, *.xls</em>
                        </template>

                        <template v-slot:upload-label>
                            <icon class="large-svg" name="upload" /><br />
                            <strong>Klik for at vælge dine filer</strong><br />
                            <small>... eller træk og slip dem her.</small>
                        </template>
                        <template v-slot:upload-title>
                            Klik for at vælge dine filer eller træk og slip dem her.
                        </template>
                        <template v-slot:upload-saving-singluar>
                            Uploader fil...
                        </template>
                        <template v-slot:upload-saving-plural>
                            Uploader flere filer...
                        </template>
                        <template v-slot:upload-success-singluar>
                            Fil uploadet.
                        </template>
                        <template v-slot:upload-success-plural>
                            Filer uploadet.
                        </template>
                        <template v-slot:upload-failed>
                            Upload fejlede desværre.
                        </template>
                    </n-files>

                    <p class="dfiles-list__empty" v-if="enquiryDocuments.length === 0">Der er ikke tilføjet nogle filer endnu.</p>

                    <template v-if="enquiryDocumentsSorted.length > 0">
                        <div class="document__options">
                            <div class="document__option">
                                <input type="checkbox" id="file-metadata" :checked="enquiryDocumentsMetadataVisible" @change="documentListMetadataToggle" />
                                <label for="file-metadata">Vis filoplysninger</label>
                            </div>
                        </div>

                        <ul class="document__list">
                            <li class="document__item" v-for="file in enquiryDocumentsSorted" :key="file.id">
                                <div class="document__image">
                                    <div class="document__loader"></div>
                                    <img v-if="file.contentType.startsWith('image')" :src="file.dataSource" alt="" loading="lazy" />
                                    <icon v-else :name="getIcon(file)" :class="getIcon(file)" />
                                    <button v-if="file.contentType.startsWith('image')" @click="documentOpen(file)"><span class="visually-hidden">Vis billede</span></button>
                                    <button v-else @click="documentDownload(file)"><span class="visually-hidden">Download fil</span></button>
                                    <div class="document__icon" v-if="file.description && !enquiryDocumentsMetadataVisible" :title="'Kommentar: ' + file.description">
                                        <icon name="info-2" />
                                    </div>
                                </div>
                                <div class="document__info" v-if="enquiryDocumentsMetadataVisible">
                                    <div class="document__name" :title="file.contentType.startsWith('image') ? $filters.formatDateTime(file.photoTaken) : file.name">
                                        <template v-if="file.contentType.startsWith('image')">{{ file.photoTaken | formatDateTime }}</template>
                                        <template v-else>{{ file.name }}</template>
                                    </div>
                                    <div class="document__date"><strong>Upload:</strong> <time :datetime="file.dateCreated">{{ file.dateCreated | formatDateShort }}</time></div>
                                    <div class="document__description" v-if="file.description"><strong>Kommentar:</strong> <span class="truncate" :title="file.description">{{ file.description }}</span></div>
                                </div>
                                <div class="document__actions">
                                    <div class="document__setting">
                                        <input type="checkbox" :id="`file-share-${file.id}`" :checked="file.shared" @click="shareToggle(file)" />
                                        <label :for="`file-share-${file.id}`">Vis på opgave</label>
                                    </div>

                                    <button title="Tilføj kommentar" v-if="checkDescriptionAvailability(file)" @click="commentFile(file)">
                                        <span class="visually-hidden">Tilføj kommentar</span>
                                        <icon name="pencil" />
                                    </button>
                                    <button title="Download fil" @click="documentDownload(file)">
                                        <span class="visually-hidden">Download</span>
                                        <icon name="download-arrow" />
                                    </button>
                                    <button class="delete" title="Slet fil" @click="documentRemove(file.id)">
                                        <span class="visually-hidden">Slet</span>
                                        <icon name="trash" />
                                    </button>
                                </div>
                            </li>
                        </ul>
                    </template>
                </section>

                <!-- ------------- step 4 ------------- -->
                <section v-if="step === 4">
                    <h2 style="margin-bottom: 0px;">Opgave</h2>

                    <div v-if="enquiry.taskId && enquiryTask.length < 1 && !taskToRemoveFromEnquiry">
                        <span style="font-weight: 400; color: #999999; font-size: 1.3rem">
                            Henter opgave...
                        </span>
                    </div>
                    <div v-else>
                        <div class="grid-x" style="margin-bottom: 10px;">
                            <div v-if="!(enquiryTask && enquiryTask.length > 0 && !selectExistingTask)" class="cell large-9">
                                <p v-if="selectExistingTask">Listen nedenfor indeholder opgaver, der ligger inden for {{ distanceFromEnquiry }} meter. Tilknyt en opgave ved at vælge i listen og tryk på gem</p>
                                <p v-else>Tilknyt ny eller eksisterende opgave til denne henvendelse</p>
                            </div>
                            <div v-show="enquiryTask && enquiryTask.length > 0 && !selectExistingTask" class="cell auto" style="display:flex; justify-content: flex-end">
                                <button class="button button-simplified large-width-label" @click="removeEnquiryTask()">
                                    <icon class="small-svg fill-dark-gray" name="bin" />
                                    Fjern opgave
                                </button>
                            </div>
                            <div class="cell large-2" v-show="selectExistingTask && (enquiryTask && enquiryTask.length < 1)">
                                <button class="button button-simplified large-width-label" @click="selectExistingTask = !selectExistingTask">
                                    <icon class="small-svg fill-dark-gray" name="bin" />
                                    Fortryd
                                </button>
                            </div>
                        </div>
                        <div class="grid-x" v-if="enquiryTask && enquiryTask.length > 0">
                            <div class="cell form">
                                <div class="cell large-11">
                                    <enquiry-tasks-list />
                                </div>
                            </div>
                        </div>
                        <div v-else class="grid-x">
                            <div v-if="!selectExistingTask" class="cell">
                                <div class="">
                                    <div class="grid-x" >
                                        <div class="cell auto flexbox">
                                            <a @click="createTaskFromEnquiry()" class="button custom-button task no-label flex-column flexbox-center">
                                                <icon class="large-svg fill-primary" name="task-add" />
                                                <span>Ny opgave</span>
                                            </a>
                                            <a @click="selectExistingTask = !selectExistingTask" class="button custom-button task no-label flex-column flexbox-center">
                                                <icon class="large-svg fill-primary" name="task" />
                                                <span>Eksisterende opgave</span>
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div v-else class="cell large-11">
                                <tasks-list-simple @attachTask="attachExistingTaskToEnquiry" v-model="tasks" />
                            </div>
                        </div>
                    </div>
                </section>
            </main>
            <div v-show="loading" class="loading-div">
                <h1 class="loading-header">Henter henvendelse...</h1>
            </div>
        </div>

        <base-map ref="baseMap" :config="mapConfig" :existingWork="existingWork" :locationWkt="enquiry.locationWkt" :supplyCode="selectedSupplyType.code" :supplyTypeId="selectedSupplyType.id" />
        <modal v-if="showModal" :type="modalType" :data="modalData" @close="closeModal" />
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import NInputAddress from './../components/shared/NInputAddress.vue'
import baseMap from './../components/shared/map/base-map.vue'
import { NMapConfig, NMapButton, NMapDraw, NMapFeature, NMapCoordinates } from 'ncomponents-map'
import NFiles from './../components/shared/files/files.vue'
import { wait } from '@/mixins/shared/helpers'
import changeTracking from '@/mixins/shared/change-tracking'
import guideNav from './../components/shared/nav/guide-nav.vue'
import supplyType from './../components/shared/supplytype/supplytype.vue'
import enquiryTasks from './../components/shared/list/list-enquiry-tasks.vue'
import modal from './../components/shared/modal/modal.vue'
import historyLog from './../components/shared/history/history.vue'
import tasksSimple from './../components/shared/list/list-tasks-simple.vue'
import BaseSplitButton from '../components/base/BaseSplitButton.vue'
import BaseDatePicker from '@/components/base/BaseDatePicker.vue'
import NFilter from '@/components/base/NFilter.vue'

export default {
    name: 'enquiry',
    components: {
        modal,
        'n-input-address': NInputAddress,
        'base-map': baseMap,
        'NFiles': NFiles,
        'guide-nav': guideNav,
        'supply-type': supplyType,
        'enquiry-tasks-list': enquiryTasks,
        'tasks-list-simple': tasksSimple,
        'history-log': historyLog,
        'base-split-button': BaseSplitButton,
        BaseDatePicker,
        NFilter
    },
    mixins: [changeTracking],
    data () {
        return {
            step: 0,
            dataPropertyName: 'enquiry', // Point to your data property
            address: null,
            enquiry: {
                address1: null,
                addressGuid: null,
                businessAreaTypeId: null,
                city: null,
                clientAnonymous: true,
                clientEmail: null,
                clientName: null,
                clientPhone: null,
                clientResponse: false,
                complaint: false,
                locationWkt: null,
                occurrenceDateTime: null,
                ownerUserId: null,
                postalCode: null,
                receivedByUserId: null,
                showDescriptionOnRequisition: false
            },
            addressObject: {
                oneLineAddress: ''
            },
            existingEnquiry: false,
            validEmailSaved: true,
            removingEnquiry: false,
            contactTypesFiltered: [],
            estateTypesFiltered: [],
            problemTypesFiltered: [],
            weatherTypesFiltered: [],
            selectedSupplyTypeDescription: '',
            createTaskFromEnquiryVal: false,
            statusTypeId: '',
            statusText: '',
            selectedAddress: false,
            selectedExistingTask: null,
            // loading
            loading: false,
            // save
            saveStatus: 'Gem',
            savedTimestamp: null,
            enquiryWkt: undefined,
            showModal: false,
            responsiblesFiltered: [],
            selectExistingTask: false,
            userKommunekode: null,
            taskToRemoveFromEnquiry: false,
            showFields: {
                weatherType: true,
                clientType: true,
                estateType: true,
                complaint: true
            },
            distanceFromEnquiry: 500,
            mapConfig: null,
            existingWork: null,
            showInfo: false,

            uploadStatusTimer: null,
            uploadStatus: 'initial',
            uploadErrorResponse: null,
            addressConfig: {
                minLength: 2,
                autoSelectFirst: true,
                isAsync: true,

                options: {
                    per_side: 40,
                    srid: 25832,
                    type: 'adgangsadresse',
                    kommunekode: null
                }
            },
            addressList: [],

            enquiryDocumentsMetadataVisible: true,
            sortKey: 'dateCreated',
            sortDirection: 'DESC'
        }
    },
    computed: {
        ...mapGetters({
            applicationSettings: 'getApplicationSettings',
            organizationUserList: 'caseWorkerList',
            supplyTypes: 'getSysBusinessAreaTypeList',
            contactTypes: 'getCClientTypeList',
            estateTypes: 'getEstateTypes',
            problemTypes: 'getProblemTypes',
            weatherTypes: 'getWeatherTypes',
            selectedSupplyType: 'selectedSupplyType',
            statusTypeList: 'getSysRequestStatusTypeList',
            userId: 'getUserId',
            selectedSupplyTypeId: 'getSelectedSupplyTypeId',
            enquiryDocuments: 'getEnquiryDocuments',
            selectedEnquiry: 'getSelectedEnquiry',
            temporaryEnquiry: 'getEnquiryTemp',
            userInfo: 'userInfo',
            tasks: 'getTaskSimple',
            existingEnquiriesFromAddress: 'existingEnquiriesFromAddress',
            existingTasksFromAddress: 'existingTasksFromAddress',
            permissions: 'permissions',
            enquiryTask: 'getEnquiryTask',
            enquiryLog: 'getSelectedEnquiryLog',
            showFieldSettings: 'getShowFieldSettings'
        }),

        recentContactTypes () {
            const recent = this.getRecentContactTypesFromStorage()
            if (this.contactTypesFiltered && this.contactTypesFiltered.length > 0 && recent.length > 0) {
                const totalCount = this.contactTypesFiltered.length
                const maxRecent = totalCount >= 30 ? 10 : (totalCount >= 15 ? 5 : 0)

                if (maxRecent === 0) return []

                return recent
                    .map(contactTypeId => this.contactTypesFiltered.find(a => a.id === contactTypeId))
                    .filter(contactType => contactType !== undefined)
                    .slice(0, maxRecent)
            }
            return []
        },
        otherContactTypes () {
            if (this.contactTypesFiltered && this.contactTypesFiltered.length > 0) {
                if (this.contactTypesFiltered.length < 15) {
                    return this.contactTypesFiltered
                }
                if (this.recentContactTypes.length > 0) {
                    const recentIds = this.recentContactTypes.map(a => a.id)
                    return this.contactTypesFiltered.filter(contactType => !recentIds.includes(contactType.id))
                }
            }
            return this.contactTypesFiltered
        },
        recentEstateTypes () {
            const recent = this.getRecentEstateTypesFromStorage()
            if (this.estateTypesFiltered && this.estateTypesFiltered.length > 0 && recent.length > 0) {
                const totalCount = this.estateTypesFiltered.length
                const maxRecent = totalCount >= 30 ? 10 : (totalCount >= 15 ? 5 : 0)

                if (maxRecent === 0) return []

                return recent
                    .map(estateTypeId => this.estateTypesFiltered.find(e => e.id === estateTypeId))
                    .filter(estateType => estateType !== undefined)
                    .slice(0, maxRecent)
            }
            return []
        },
        otherEstateTypes () {
            if (this.estateTypesFiltered && this.estateTypesFiltered.length > 0) {
                if (this.estateTypesFiltered.length < 15) {
                    return this.estateTypesFiltered
                }
                if (this.recentEstateTypes.length > 0) {
                    const recentIds = this.recentEstateTypes.map(e => e.id)
                    return this.estateTypesFiltered.filter(estateType => !recentIds.includes(estateType.id))
                }
            }
            return this.estateTypesFiltered
        },
        email () {
            return this.enquiry.clientEmail
        },
        iconObj () {
            return [
                { icon: 'history', text: 'Historik', active: true },
                { icon: 'user', text: 'Anmelder', active: true },
                { icon: 'speechbubble', text: 'Problem', active: true },
                { icon: 'pictures', text: 'Dokumenter', active: true, highlight: false, counter: this.enquiryDocuments.length > 0 ? this.enquiryDocuments.length : null },
                { icon: 'task', text: 'Opgave', active: true }
            ]
        },

        enquiryResponsible () {
            if (this.responsiblesFiltered.length < 1 || !this.enquiry || !this.enquiry.ownerUserId) {
                return ''
            }

            const responsible = this.responsiblesFiltered.find(responsible => responsible.userId === this.enquiry.ownerUserId)

            if (responsible) {
                return responsible.name
            } else {
                return ''
            }
        },

        enquiryDocumentsSorted () {
            return [...this.enquiryDocuments].sort((a, b) => {
                let modifier = 1

                if (this.sortDirection === 'DESC') {
                    modifier = -1
                }

                if (a[this.sortKey] < b[this.sortKey]) {
                    return -1 * modifier
                }
                if (a[this.sortKey] > b[this.sortKey]) {
                    return 1 * modifier
                }

                return 0
            })
        }
    },
    methods: {
        ...mapActions({
            getEnquiry: 'getEnquiry',
            clearEnquiryUUID: 'clearEnquiryUUID',
            clearEnquiryDocuments: 'clearEnquiryDocuments',
            setSupplyType: 'setSelectedSupplyTypeFromId',
            saveEnquiry: 'createUpdateEnquiry',
            setEnquiryTask: 'setEnquiryTask',
            getTasksFromEnquiry: 'getQueryTaskListFromEnquiry',
            setEnquiryStatus: 'setEnquiryStatus',
            setEnquiryUUID: 'setEnquiryUUID',
            // other
            getCaseworkers: 'getCaseworkers',
            getCurrentOrganizationList: 'getCurrentOrganizationList',
            getAllTypeLists: 'getAllTypeLists',
            setEnquiryAddressToTask: 'setEnquiryAddressToTask',
            getExistingWorkFromAddress: 'getExistingWorkFromAddress',
            setRequestOnTaskListFromEnquiry: 'setRequestOnTaskListFromEnquiry',
            getCurrentUserInfo: 'getCurrentUserInfo',
            getOrganizationList: 'getAssociatedOrganizationList',
            setTempEnquiry: 'setTempEnquiry',
            setEnquirySupplyType: 'setEnquirySupplyType',
            addTaskToRequest: 'addTaskToRequest',
            setEnquiryLogList: 'setEnquiryLogList',
            removeTaskFromRequest: 'removeTaskFromRequest',
            setRemoveRequestOnTaskListFromEnquiry: 'setRemoveRequestOnTaskListFromEnquiry',
            printCaseHistoryRequest: 'printCaseHistoryRequest',

            // FILES
            documentAdd: 'enquiryDocumentAdd',
            documentRemove: 'enquiryDocumentRemove',
            documentOpen: 'enquiryDocumentOpen',
            documentDownload: 'enquiryDocumentDownload',
            documentDescriptionUpdate: 'enquiryDocumentDescriptionUpdate',

            documentShare: 'enquiryDocumentShareUpdate'
        }),

        getRecentContactTypesFromStorage () {
            try {
                const stored = localStorage.getItem(`recent${String(this.selectedSupplyType.systemName).charAt(0).toUpperCase() + String(this.selectedSupplyType.systemName).slice(1)}ContactTypes`)
                return stored ? JSON.parse(stored) : []
            } catch (e) {
                console.error('Error reading recent contact types from localStorage:', e)
                return []
            }
        },
        saveRecentContactTypeToStorage (contactTypeId) {
            if (!contactTypeId) return

            try {
                let recent = this.getRecentContactTypesFromStorage()

                // Remove if already exists
                recent = recent.filter(id => id !== contactTypeId)

                // Add to beginning
                recent.unshift(contactTypeId)

                // Keep only the 10 most recent
                recent = recent.slice(0, 10)

                localStorage.setItem(`recent${String(this.selectedSupplyType.systemName).charAt(0).toUpperCase() + String(this.selectedSupplyType.systemName).slice(1)}ContactTypes`, JSON.stringify(recent))
            } catch (e) {
                console.error('Error saving recent contact type to localStorage:', e)
            }
        },
        getRecentEstateTypesFromStorage () {
            try {
                const stored = localStorage.getItem(`recent${String(this.selectedSupplyType.systemName).charAt(0).toUpperCase() + String(this.selectedSupplyType.systemName).slice(1)}EstateTypes`)
                return stored ? JSON.parse(stored) : []
            } catch (e) {
                console.error('Error reading recent estate types from localStorage:', e)
                return []
            }
        },
        saveRecentEstateTypeToStorage (estateTypeId) {
            if (!estateTypeId) return

            try {
                let recent = this.getRecentEstateTypesFromStorage()

                // Remove if already exists
                recent = recent.filter(id => id !== estateTypeId)

                // Add to beginning
                recent.unshift(estateTypeId)

                // Keep only the 10 most recent
                recent = recent.slice(0, 10)

                localStorage.setItem(`recent${String(this.selectedSupplyType.systemName).charAt(0).toUpperCase() + String(this.selectedSupplyType.systemName).slice(1)}EstateTypes`, JSON.stringify(recent))
            } catch (e) {
                console.error('Error saving recent estate type to localStorage:', e)
            }
        },

        splitClick () {
            console.log('Split button clicked')
        },
        createRequisition (problemTypeId) {
            const problemType = this.problemTypesFiltered.find(problemType => problemType.cProblemTypeId === problemTypeId)

            if (problemType && problemType.createRequisition) {
                return 'Ja'
            }
            return 'Nej'
        },

        problemTypeSelected (problemTypeId) {
            const problemType = this.problemTypesFiltered.find(problemType => problemType.cProblemTypeId === problemTypeId)
            if (problemType.taskTemplateOwnerUserId) {
                this.enquiry.ownerUserId = problemType.taskTemplateOwnerUserId
            }
        },

        statusColor (status) {
            if (status) {
                if (status.toLowerCase() === 'oprettet') {
                    return 'blue-status'
                }
                if (status.toLowerCase() === 'drifttiltag besluttet') {
                    return 'green-status'
                }
                if (status.toLowerCase() === 'afsluttet') {
                    return 'gray-status'
                }
                if (status.toLowerCase() === 'afvist - vedrører ikke forsyningen') {
                    return 'gray-status'
                }
            }
            return 'blue-status'
        },
        openModal (type, data) {
            this.showModal = true
            this.modalType = type
            this.modalData = data
        },
        closeModal () {
            if (this.modalData.updating) {
                this.documentDescriptionUpdate(this.modalData)
            }

            this.showModal = false
        },
        getUserInfo () {
            const foundUserFromList = this.organizationUserList.find(user => user.userId === this.enquiry.ownerUserId)
            if (foundUserFromList) {
                this.enquiry.clientName = foundUserFromList.name
                this.enquiry.clientPhone = foundUserFromList.phoneNumber
                this.enquiry.clientEmail = foundUserFromList.email
                this.enquiry.clientAnonymous = false
                this.validEmailSaved = this.isValidEmail()
            }
        },
        async printCaseHistoryHandler () {
            try {
                this.openModal('handler', 'Danner udskrift for sagsforløb...')
                await this.printCaseHistoryRequest(this.enquiry.requestId)
                this.closeModal()
            } catch (error) {
                console.error('Print case history', error)
                this.openModal('handler', 'Kunne ikke danne udskrift. Prøv igen')
                setTimeout(() => {
                    this.closeModal()
                }, 2500)
            }
        },
        translateAddressData (address) {
            if (!address) {
                return
            }
            return {
                id: address.id,
                street: address.raw.husnummer.vejnavn,
                // href: address.data.href,
                streetNumber: address.houseNumber,
                // floor: address.data.etage,
                // door: address.data.dør,
                zipCode: address.postalCode,
                city: address.raw.husnummer.postnummer.navn,
                secondStreet: address.raw.husnummer.supplerendebynavn.navn,
                oneLineAddress: address.raw.husnummer.vejnavn + ' ' + address.houseNumber,
                type: address.type
            }
        },
        changedStep (result) {
            this.step = result
        },
        getListDescriptionFromId (list, id) {
            if (id !== null) {
                var description = ''
                var listType = ''
                var listTypeId = ''
                switch (list) {
                case 'contactType':
                    listType = this.contactTypes
                    listTypeId = 'cClientTypeId'
                    break
                case 'estateType':
                    listType = this.estateTypes
                    listTypeId = 'cEstateTypeId'
                    break
                case 'problemType':
                    listType = this.problemTypes
                    listTypeId = 'cProblemTypeId'
                    break
                case 'weatherType':
                    listType = this.weatherTypes
                    listTypeId = 'cWeatherTypeId'
                    break
                }
                for (var i in listType) {
                    if (listType[i]) {
                        if (listType[i].businessAreaTypeId === this.selectedSupplyTypeId) {
                            description = listType[i].codeList.filter(code => code[listTypeId] === id)
                            if (description.length > 0) {
                                description = description[0].description
                            }
                        }
                    }
                }
                if (description === '' || description.length === 0) {
                    description = 'Andet'
                }
                return description
            } else {
                return 0
            }
        },
        onWktSelected (value) {
            this.enquiry.locationWkt = value
        },
        removeEnquiryTask () {
            this.taskToRemoveFromEnquiry = true
            this.setEnquiryTask()
            this.selectExistingTask = false
            this.selectedExistingTask = null
            this.setRemoveRequestOnTaskListFromEnquiry()
            // this.enquiry.requestId = null
            // this.enquiry.requestVersionStamp = null
        },
        getExistingEnquiry (enquiryId, copy) {
            this.getEnquiry({
                id: enquiryId,
                copy: copy
            })
                .then(() => {
                    if (!copy) {
                        this.existingEnquiry = true
                    }
                })
                .then(() => {
                    this.enquiry = { ...this.selectedEnquiry }
                    // uses tempEnquiry in Vuex to get this.enquiry for validating save
                    this.setTempEnquiry(this.enquiry)

                    return true
                })
                .then(() => {
                // Set attached task if any
                    if (this.permissions.tasks.enabled && this.enquiry.taskId) {
                        this.setAttachedTask()
                    }
                    this.setSupplyType(this.enquiry.businessAreaTypeId)
                        .then(() => {
                            return this.selectedSupplyTypeId
                        })
                        .then(() => {
                            if (this.enquiry.address1) {
                                let fullAddress = this.enquiry.address1

                                if (this.enquiry.address2) {
                                    fullAddress += ', ' + this.enquiry.address2
                                }
                                if (this.enquiry.postalCode) {
                                    fullAddress += ', ' + this.enquiry.postalCode
                                }
                                if (this.enquiry.city) {
                                    fullAddress += ' ' + this.enquiry.city
                                }
                                this.addressObject.oneLineAddress = fullAddress
                                this.addressObject.id = this.enquiry.addressGuid
                                this.addressObject.city = this.enquiry.city
                                this.addressObject.zipCode = this.enquiry.postalCode
                                this.addressObject.street = this.enquiry.address1
                                this.addressObject.secondStreet = this.enquiry.address2
                                this.$nextTick(() => {
                                    if (this.$refs.addressInput) {
                                        this.$refs.addressInput.setText(fullAddress)
                                    }
                                })

                                let addressObj = {
                                    address1: this.enquiry.address1,
                                    address2: this.enquiry.address2,
                                    addressGuid: this.enquiry.addressGuid,
                                    city: this.enquiry.city,
                                    postalCode: this.enquiry.postalCode,
                                    oneLineAddress: fullAddress
                                }
                                this.setEnquiryAddressToTask(addressObj)

                                // Getting existing enquiries and tasks on the address
                                new Promise((resolve, reject) => {
                                    resolve(this.getExistingWorkFromAddress(this.addressObject))
                                })
                                    .then(() => {
                                        let existingWorkObject = {}
                                        if (this.existingEnquiriesFromAddress.length > 0) {
                                            existingWorkObject.enquiries = this.existingEnquiriesFromAddress

                                            // Filter out the one enquiry the user is currently operating in
                                            if (enquiryId) {
                                                existingWorkObject.enquiries = existingWorkObject.enquiries.filter(enquiry => enquiry.requestId !== enquiryId)
                                            }
                                        }
                                        if (this.existingTasksFromAddress.length > 0) {
                                            existingWorkObject.tasks = this.existingTasksFromAddress
                                        }
                                        this.existingWork = (existingWorkObject.enquiries || existingWorkObject.tasks) ? existingWorkObject : null
                                    })
                            }

                            // We set the responsible
                            if (this.enquiry.ownerActive) {
                                this.enquiry.ownerUserId = !this.selectedEnquiry.ownerUserId ? '' : this.selectedEnquiry.ownerUserId
                            } else {
                                this.responsiblesFiltered = [{
                                    userId: this.enquiry.ownerUserId,
                                    displayName: this.enquiry.ownerName + ' (INAKTIV)'
                                }, ...this.responsiblesFiltered]
                            }
                            this.$store.state.enquiry.enquirySelected.ownerUserId = this.selectedEnquiry.ownerUserId
                        })
                })
                .then(() => {
                    this.initializeTracking(this.enquiry)
                })
        },
        setAttachedTask () {
            new Promise((resolve, reject) => {
                resolve(this.getTasksFromEnquiry({ whereTaskId: this.enquiry.taskId }))
            }).then(() => {
                if (this.tasks.length > 0) {
                    this.setEnquiryTask(this.enquiry.taskId)
                }
            })
        },
        populateLists (newSupply) {
            if (newSupply !== undefined && newSupply.id) {
                this.selectedSupplyTypeDescription = newSupply.description
                this.contactTypesFiltered = this.contactTypes
                this.estateTypesFiltered = this.estateTypes
                this.weatherTypesFiltered = this.weatherTypes
                this.problemTypesFiltered = this.problemTypes

                if (this.contactTypesFiltered[newSupply.systemName].businessAreaTypeId === newSupply.id) {
                    this.contactTypesFiltered = this.contactTypesFiltered[newSupply.systemName].codeList
                }
                if (Array.isArray(this.contactTypesFiltered)) {
                    this.contactTypesFiltered.sort(function (a, b) {
                        if (a.description < b.description) { return -1 }
                        if (a.description > b.description) { return 1 }
                        return 0
                    })
                }
                if (this.estateTypesFiltered[newSupply.systemName].businessAreaTypeId === newSupply.id) {
                    this.estateTypesFiltered = this.estateTypesFiltered[newSupply.systemName].codeList
                }
                if (Array.isArray(this.estateTypesFiltered)) {
                    this.estateTypesFiltered.sort(function (a, b) {
                        if (a.description < b.description) { return -1 }
                        if (a.description > b.description) { return 1 }
                        return 0
                    })
                }
                if (this.weatherTypesFiltered[newSupply.systemName].businessAreaTypeId === newSupply.id) {
                    this.weatherTypesFiltered = this.weatherTypesFiltered[newSupply.systemName].codeList
                }
                if (Array.isArray(this.weatherTypesFiltered)) {
                    this.weatherTypesFiltered.sort(function (a, b) {
                        if (a.description < b.description) { return -1 }
                        if (a.description > b.description) { return 1 }
                        return 0
                    })
                }
                if (this.problemTypesFiltered[newSupply.systemName].businessAreaTypeId === newSupply.id) {
                    this.problemTypesFiltered = this.problemTypesFiltered[newSupply.systemName].codeList
                }
                if (Array.isArray(this.problemTypesFiltered)) {
                    this.problemTypesFiltered.sort(function (a, b) {
                        if (a.description < b.description) {
                            if (a.description.toLowerCase() === 'andet') {
                                return 1
                            } else {
                                return -1
                            }
                        }
                        if (a.description > b.description) {
                            return 1
                        }
                        return 0
                    })

                    // Selecting the same problemtype if any when switching supply type
                    if (this.problemTypesFiltered) {
                        const newProblemType = this.problemTypesFiltered.find(problemtype => problemtype.cProblemTypeId === this.enquiry.problemTypeId)
                        if (newProblemType) {
                            this.enquiry.problemTypeId = newProblemType.cProblemTypeId
                        } else if (Array.isArray(this.problemTypesFiltered) && this.problemTypesFiltered.length > 0) {
                            this.enquiry.problemTypeId = null
                        }
                    }
                }
            }
        },
        showHideFields () {
            this.showFields.weatherType = true
            this.showFields.clientType = true
            this.showFields.estateType = true
            this.showFields.complaint = true

            if (this.selectedSupplyType.systemName) {
                if (this.showFieldSettings) {
                    this.showFieldSettings.forEach(a => {
                        if (a.BusinessAreaSystemName === this.selectedSupplyType.systemName) {
                            this.showFields.weatherType = a.RequestShowWeatherType
                            this.showFields.clientType = a.RequestShowClientType
                            this.showFields.estateType = a.RequestShowEstateType
                            this.showFields.complaint = a.RequestShowComplaint
                        }
                    })
                }
            }
        },
        createTaskFromEnquiry () {
            this.createTaskFromEnquiryVal = true
            this.selectedExistingTask = false
            this.taskToRemoveFromEnquiry = false
            this.save()
        },
        attachExistingTaskToEnquiry (task) {
            this.createTaskFromEnquiryVal = true
            this.selectedExistingTask = task
            this.taskToRemoveFromEnquiry = false
            this.save()
        },
        deleteEnquiry () {
            this.removingEnquiry = true
            this.openModal('enquiry-modal-delete', { enquiry: this.enquiry })
        },
        save () {
            if (this.saveStatus !== 'Gemmer...') {
                if (!this.isValidEmail()) {
                    this.validEmailSaved = false
                } else {
                    this.saveStatus = 'Gemmer...'
                    if (this.selectedSupplyType) {
                        this.enquiry.businessAreaTypeId = this.selectedSupplyType.id
                    }
                    // Userid for the user first time saving the enquiry
                    if (!this.enquiry.receivedByUserId) {
                        this.enquiry.receivedByUserId = this.userId
                    }
                    // Date picked
                    if (!this.enquiry.occurrenceDateTime) {
                        this.enquiry.occurrenceDateTime = new Date()
                    }
                    // Id for the selected values in lists
                    const enquiryProblemType = this.problemTypesFiltered.filter(problemtype => problemtype.cProblemTypeId === this.enquiry.problemTypeId)[0]
                    if (enquiryProblemType) {
                        this.enquiry.problemTypeId = enquiryProblemType.cProblemTypeId
                        this.enquiry.problemType = enquiryProblemType.description
                    }
                    // Address
                    let addressObj = {}
                    setTimeout(() => {
                        if (this.address || this.addressObject.oneLineAddress) {
                            if (this.addressObject.street) {
                                this.enquiry.address1 = this.addressObject.street
                            }
                            if (this.addressObject.door && !this.addressObject.floor) {
                                this.enquiry.address1 += ', ' + this.addressObject.door
                            }
                            if (this.addressObject.floor && !this.addressObject.door) {
                                this.enquiry.address1 += ', ' + this.addressObject.floor
                            }
                            if (this.addressObject.door && this.addressObject.floor) {
                                this.enquiry.address1 += ', ' + this.addressObject.floor + '. ' + this.addressObject.door
                            }
                            if (this.enquiry.address1) {
                                addressObj.address1 = this.enquiry.address1
                            }
                            if (this.addressObject.secondStreet) {
                                this.enquiry.address2 = this.addressObject.secondStreet
                                addressObj.address2 = this.enquiry.address2
                            }
                            if (this.addressObject.id) {
                                this.enquiry.addressGuid = this.addressObject.id
                                addressObj.addressGuid = this.addressObject.id
                            }
                            if (this.addressObject.city) {
                                this.enquiry.city = this.addressObject.city
                                addressObj.city = this.addressObject.city
                            }
                            if (this.addressObject.zipCode) {
                                this.enquiry.postalCode = this.addressObject.zipCode
                                addressObj.postalCode = this.addressObject.zipCode
                            }
                            if (this.addressObject.postalCode) {
                                this.enquiry.postalCode = this.addressObject.postalCode
                                addressObj.postalCode = this.addressObject.postalCode
                            }
                            addressObj.oneLineAddress = this.addressObject.oneLineAddress
                            addressObj.wkt = this.enquiry.locationWkt
                        } else {
                            this.enquiry.address2 = undefined
                            this.enquiry.addressGuid = undefined
                            this.enquiry.city = undefined
                            this.enquiry.postalCode = undefined
                            this.enquiry.address1 = undefined
                        }
                        if (enquiryProblemType && enquiryProblemType.taskTemplateId && !this.enquiry.locationWkt) {
                            this.$notify({
                                group: 'info',
                                type: 'error',
                                text: 'Den valgte problemtype kræver, at du enten angiver en adresse eller udpeger et punkt på kortet.'
                            })

                            this.saveStatus = 'Gem'

                            return false
                        }
                        if (!this.enquiry.ownerUserId) {
                            window.alert('Du skal vælge en aktiv sagsbehandler for at kunne gemme henvendelsen.')
                            this.saveStatus = 'Gem'
                            return false
                        }

                        if ((!this.enquiry.address1 || !this.enquiry.addressGuid || !this.enquiry.city || !this.enquiry.postalCode) && !this.enquiry.locationWkt) {
                            window.alert('Du har ikke indtastet eller valgt en præcis adresse eller punkt.')
                            this.saveStatus = 'Gem'
                            return false
                        }

                        this.saveEnquiry(this.enquiry).then(() => {
                            // Save contact type to recent list
                            if (this.enquiry.clientTypeId) {
                                this.saveRecentContactTypeToStorage(this.enquiry.clientTypeId)
                            }
                            // Save estate type to recent list
                            if (this.enquiry.estateTypeId) {
                                this.saveRecentEstateTypeToStorage(this.enquiry.estateTypeId)
                            }

                            this.saveStatus = 'Gemt'
                            this.savedTimestamp = new Date()
                            setTimeout(() => {
                                this.saveStatus = 'Gem'
                            }, 3000)
                            if (!this.enquiry.hasOwnProperty('ownerActive')) {
                                this.enquiry.ownerActive = true
                            }
                            this.enquiry.requestId = this.$store.state.enquiry.enquirySelected.requestId
                            this.enquiry.requestVersionStamp = this.$store.state.enquiry.enquirySelected.requestVersionStamp

                            this.resetChanges()

                            if (!this.existingEnquiry) {
                                this.existingEnquiry = true

                                // Include ID in URL when saving a new enquiry
                                this.$router.replace({
                                    name: 'enquiryid',
                                    params: {
                                        id: this.enquiry.requestId
                                    }
                                })
                            }

                            this.setEnquiryLogList(this.enquiry.requestId)

                            if (this.createTaskFromEnquiryVal) {
                                if (this.selectedExistingTask) {
                                    let data = { requestId: this.enquiry.requestId, taskId: this.selectedExistingTask.taskId }
                                    new Promise((resolve, reject) => {
                                        resolve(this.addTaskToRequest(data))
                                    }).then((response) => {
                                        this.enquiry.requestId = response.data.requestId
                                        this.enquiry.requestVersionStamp = response.data.requestVersionStamp
                                        this.resetChanges()
                                        this.setEnquiryTask(this.selectedExistingTask.taskId)
                                        this.selectExistingTask = false
                                        this.setRequestOnTaskListFromEnquiry([this.enquiry])
                                    })
                                } else {
                                    this.setEnquirySupplyType(this.enquiry.businessAreaTypeId)
                                    let promiseSupply = new Promise((resolve, reject) => {
                                        resolve(this.setSupplyType(this.enquiry.businessAreaTypeId))
                                    })

                                    // set enquiry Address in Vuex so task can get it
                                    let promiseAddress = new Promise((resolve, reject) => {
                                        resolve(this.setEnquiryAddressToTask(addressObj))
                                    })
                                    let promiseTask = new Promise((resolve, reject) => {
                                        resolve(this.setRequestOnTaskListFromEnquiry([this.enquiry]))
                                    })

                                    let promises = [promiseSupply, promiseAddress, promiseTask]

                                    Promise.all(promises).then(() => {
                                        this.$router.push({name: 'task'})
                                    })
                                }
                            } else if (this.taskToRemoveFromEnquiry) {
                                this.removeTaskFromRequest()
                            }

                            if (enquiryProblemType && enquiryProblemType.taskTemplateId) {
                                this.getEnquiry({ id: this.enquiry.requestId })
                                    .then((response) => {
                                        this.enquiry.taskId = response.data.request.taskId
                                        this.resetChanges()
                                        this.setAttachedTask()
                                    })
                            }
                        }, (error) => {
                            this.saveStatus = 'Kunne ikke gemme'
                            setTimeout(() => {
                                this.saveStatus = 'Gem'
                            }, 3000)
                            console.error('Save enquiry', error)
                        })
                    }, 100)
                }
            }
        },
        openEnquiry () {
            this.statusTypeId = ''
            this.statusText = ''
            let requestStatus = ''
            for (const i in this.statusTypeList) {
                if (this.statusTypeList[i].code === 2) {
                    this.statusTypeId = this.statusTypeList[i].sysRequestStatusTypeId
                    requestStatus = this.statusTypeList[i].description
                }
            }
            this.changeEnquiryStatus(this.statusTypeId, false)
            this.enquiry.requestStatus = requestStatus
            this.enquiry.closedDate = null
        },
        changeEnquiryStatus (statusId, closed) {
            let enquiryData = {
                'requestId': this.$route.params.id,
                'requestStatusTypeId': statusId
            }
            this.setEnquiryStatus(enquiryData)
        },
        getAllUserInfo () {
            let promise1 = this.getCurrentUserInfo()
            let promise2 = this.getCurrentOrganizationList()
            let promise3 = this.getAllTypeLists()

            Promise.all([promise1, promise2, promise3]).then((values) => {
                this.getOrganizationList()
                this.getCaseworkers()
                return true
            })
        },
        setupMap () {
            this.mapConfig = new NMapConfig()
            const mapButtonLocate = new NMapButton({
                content: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48"><path fill="white" d="M24 4c-7.73 0-14 6.27-14 14 0 10.5 14 26 14 26s14-15.5 14-26c0-7.73-6.27-14-14-14zm0 19c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z"/></svg>',
                title: 'Angiv placering af problem'
            })

            mapButtonLocate.on('click', () => {
                const mapDraw = new NMapDraw({
                    type: 'Point',
                    drawStyle: NMapFeature.createMarker(new NMapCoordinates(0, 0), '#5293bc').style,
                    style: NMapFeature.createMarker(new NMapCoordinates(0, 0), '#5293bc').style
                })
                mapDraw.on('done', e => {
                    let vLayer = this.mapConfig.layers.find(l => l.manager.name === 'vector')
                    vLayer.clear()
                    vLayer.addFeature(e.feature)

                    this.enquiry.locationWkt = e.feature.wkt('EPSG:25832')
                })
                this.mapConfig.draw(mapDraw)
            })
            this.mapConfig.addButton(mapButtonLocate)
        },
        setupEnquiry () {
            this.loading = true
            this.clearEnquiryUUID()
            if (!this.$route.params.id) {
                this.step = 1
                this.iconObj.forEach(icon => {
                    if (icon.icon === 'history') {
                        icon.active = false
                    } else if (icon.icon === 'pictures') {
                        icon.disabled = true
                        icon.title = 'Du skal gemme din henvendelse, før du kan tilføje dokumenter!'
                    }
                })
            } else {
                if (this.applicationSettings.uiSettings && this.applicationSettings.uiSettings.RequestStartTabNumber !== null) {
                    this.step = this.applicationSettings.uiSettings.RequestStartTabNumber
                } else {
                    this.step = 2
                }
            }
            new Promise((resolve, reject) => {
                if (!this.$store.getters.userInfo.userId) {
                    resolve(this.getAllUserInfo())
                } else {
                    resolve(true)
                }
            }).then(() => {
                if (this.applicationSettings.uiSettings && this.applicationSettings.uiSettings.DefaultBusinessAreaTypeId !== null) {
                    this.setSupplyType(this.applicationSettings.uiSettings.DefaultBusinessAreaTypeId)
                }
                if (this.selectedSupplyType.length === 0 && this.supplyTypes.length > 0) {
                    this.selectedSupplyType = this.supplyTypes[0]
                }
                if (this.applicationSettings.showRequestDescriptionOnRequisition === '1') {
                    this.enquiry.showDescriptionOnRequisition = true
                }
            }).then(() => {
                if (this.permissions.tasks.enabled) {
                    // Get task for attach task to enquiry
                    return this.getTasksFromEnquiry({ whereDistanceWithinFromRequestId: this.$route.params.id, WhereCurrentLocationWithinDistance: this.distanceFromEnquiry, orderByColumnName: 'DistanceFromLocation' })
                }
                this.setEnquiryUUID(null)
            }).then(() => {
                return this.setEnquiryTask([])
            }).then(() => {
                if (this.$route.params.id) {
                    this.setEnquiryUUID(this.$route.params.id)
                    return this.getExistingEnquiry(this.$route.params.id)
                } else if (this.$route.query.copy) {
                    return this.getExistingEnquiry(this.$route.query.copy, true)
                } else {
                    this.enquiry.occurrenceDateTime = new Date()
                    this.setEnquiryAddressToTask({})
                    return this.populateLists(this.selectedSupplyType)
                }
            }).then(() => {
                this.responsiblesFiltered = this.organizationUserList
                if (!this.$route.params.id) {
                    let responsibleNone = this.organizationUserList.filter(user => user.userId === this.userId)
                    if (responsibleNone.length > 0) {
                        this.enquiry.ownerUserId = this.enquiry.ownerUserId || this.userId
                    } else {
                        this.enquiry.ownerUserId = null
                    }
                    this.enquiry.clientTypeId = ''
                    this.enquiry.estateTypeId = ''
                }
                setTimeout(() => {
                    const addressInput = document.querySelector('.n-input-address-input input')

                    if (addressInput) {
                        addressInput.value = this.addressObject.oneLineAddress || ''
                    }
                }, 100)
                this.loading = false
            })
            this.showHideFields()
        },

        uploadFiles (element) {
            const startTime = performance.now()
            let endTime = startTime

            clearTimeout(this.uploadStatusTimer)
            this.uploadStatus = 'saving'

            Object.values(element.files).forEach((file) => {
                const objectData = {
                    data: new FormData(),
                    dataType: file.type,
                    file: file
                }

                objectData.data.append('content', file, file.name)
                this.documentAdd(objectData)
                    .then((result) => {
                        endTime = performance.now()

                        return result
                    })
                    .then(wait(250 - (endTime - startTime > 250 ? 250 : endTime - startTime))) // Make sure spinner is shown at least 250ms
                    .then(() => {
                        this.uploadStatus = 'success'
                        document.getElementById(this.$children.find(child => child.$options.name === 'NFiles').$children.find(child => child.inputId).inputId).value = ''
                        this.uploadStatusTimer = setTimeout(() => {
                            this.uploadStatus = 'initial'
                            this.save()
                        }, 3500)
                    })
                    .catch((error) => {
                        this.uploadErrorResponse = error
                        this.uploadStatus = 'failed'
                        this.uploadStatusTimer = setTimeout(() => {
                            this.uploadStatus = 'initial'
                        }, 5000)
                    })
            })
        },
        getIcon (file) {
            // Determine icon by mime type
            let iconName = 'unknown'

            if (file.contentType.indexOf('pdf') > -1) {
                iconName = 'pdf'
            } else if (file.contentType.indexOf('officedocument') > -1 && file.contentType.indexOf('.document') > -1) {
                iconName = 'doc'
            } else if (file.contentType.indexOf('officedocument') > -1 && file.contentType.indexOf('.presentation') > -1) {
                iconName = 'ppt'
            } else if (file.contentType.indexOf('officedocument') > -1 && file.contentType.indexOf('.sheet') > -1) {
                iconName = 'xls'
            } else if (file.contentType.indexOf('csv') > -1) {
                iconName = 'csv'
            } else if (file.contentType.indexOf('plain') > -1) {
                iconName = 'txt'
            } else if (file.contentType.indexOf('xml') > -1) {
                iconName = 'xml'
            }

            return iconName
        },
        checkDescriptionAvailability (file) {
            if (!file.description && this.hideDescription) {
                return false
            } else {
                return true
            }
        },
        commentFile (file) {
            this.openModal('document-modal-description', file)
        },
        shareToggle (file) {
            file.shared = event.target.checked
            this.documentShare(file)
        },
        isValidEmail () {
            let regex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

            return !(this.enquiry.clientEmail && this.enquiry.clientEmail !== '' && !regex.test(this.enquiry.clientEmail.trim()))
        },

        addressesSet (object) {
            this.selectedAddress = true
            this.addressObject = this.translateAddressData(object)
            this.enquiryWkt = `POINT (${object.utmx} ${object.utmy})`

            // Navigate to position on map
            this.$refs.baseMap.navigateTo(this.enquiryWkt)
            // Save position
            this.enquiry.locationWkt = this.enquiryWkt
            // Getting existing enquiries and tasks on the address
            if (this.addressObject) {
                if (this.addressObject.streetNumber) {
                    this.addressObject.street += ' ' + this.addressObject.streetNumber
                }
                new Promise((resolve, reject) => {
                    resolve(this.getExistingWorkFromAddress(this.addressObject))
                }).then(() => {
                    let existingWorkObject = {}

                    if (this.existingEnquiriesFromAddress.length > 0) {
                        existingWorkObject.enquiries = this.existingEnquiriesFromAddress

                        // Filter out the one enquiry the user is currently operating in
                        if (this.$route.params.id) {
                            existingWorkObject.enquiries = existingWorkObject.enquiries.filter(enquiry => enquiry.requestId !== this.$route.params.id)
                        }
                    }
                    if (this.existingTasksFromAddress.length > 0) {
                        existingWorkObject.tasks = this.existingTasksFromAddress
                    }
                    this.existingWork = (existingWorkObject.enquiries || existingWorkObject.tasks) ? existingWorkObject : null

                    let data = {
                        whereCurrentLocationPointWkt: this.enquiryWkt,
                        whereCurrentLocationWithinDistance: this.distanceFromEnquiry,
                        orderByColumnName: 'DistanceFromLocation'
                    }

                    new Promise((resolve, reject) => {
                        resolve(this.getTasksFromEnquiry(data))
                    }).then(() => {
                    })
                })
            }
        },

        documentListMetadataToggle () {
            this.enquiryDocumentsMetadataVisible = event.target.checked
            localStorage.setItem('enquiryDocumentsMetadataVisible', event.target.checked)
        }
    },
    watch: {
        selectedSupplyType (val, oldVal) {
            this.populateLists(val)
            this.showHideFields()
        },
        email () {
            if (!this.validEmailSaved && this.isValidEmail()) {
                this.validEmailSaved = true
            }
        }
    },
    filters: {
        statusName (text) {
            if (text === 'Afvist - vedrører ikke forsyningen') {
                return 'Afvist'
            } else {
                return text
            }
        }
    },
    created () {
        this.clearEnquiryDocuments()
        this.setupEnquiry()
        this.setupMap()
        if (localStorage.getItem('enquiryDocumentsMetadataVisible') === 'true') {
            this.enquiryDocumentsMetadataVisible = true
        }
    },
    beforeRouteLeave (to, from, next) {
        if (!this.removingEnquiry && to.name !== 'enquiryid' && this.hasChanges()) {
            const answer = window.confirm('Du har ændringer, som ikke er gemt. Ønsker du at gemme?')
            if (answer) {
                this.openModal('handler', 'Gemmer...')
                this.save().then(() => next())
            } else {
                next()
            }
        } else {
            next()
        }
    }
}
</script>

<style lang="scss">
@import '../scss/components/dfiles.scss';
@import '../scss/components/loader';
@import '../scss/pages/enquiry';

.page-layout .n-autocomplete {
    display: flex;

    label {

        @include sm-up() {
            width: calc(66.66667% + 20px + 20px);
        }
        @include md-up() {
            width: calc(33.33333% + 20px + 20px);
        }

        svg {
            vertical-align: top;
        }
    }
    &-wrapper {
        flex: 1 1 0px;
    }
}
</style>



// WEBPACK FOOTER //
// src/pages/enquiry.vue